import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import AuthScreen from './components/AuthScreen';
import Dashboard from './components/DashboardNew';
import DashboardInsightsPage from './components/DashboardInsightsPage';
import VisionsPage from './components/VisionsPage';
import VisionDetailPage from './components/VisionDetailPage';
import OnboardingFlow from './components/OnboardingFlow';
import ForgotPasswordScreen from './components/ForgotPasswordScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import AccountSettings from './components/AccountSettings';
import PortfolioXRay from './components/PortfolioXRay';
import CashflowPage from './components/CashflowPage';
import PlaidOAuthRedirect from './components/PlaidOAuthRedirect';
import { Community } from './components/community';
import { LearningHub, LearningAndGamesHub, RoadmapView, NodeContent } from './components/learning';
import PlaygroundContainer from './components/playground/PlaygroundContainer';
import LearningPaths from './components/learning/LearningPaths';
import LessonViewer from './components/learning/LessonViewer';
import NotesList from './components/notes/NotesList';
import MyPlanPage from './components/engagement/MyPlanPage';
import AuraCoPilot from './components/common/AuraCoPilot';
import AskAuraHint from './components/common/AskAuraHint';
import ProfilePage from './components/ProfilePage';
import AuraAssistant from './components/AuraAssistant';
import CityOfSignalsGame from './components/city-of-signals/CityOfSignalsGame';
import LevelSelector from './components/city-of-signals/LevelSelector';
import AdvisorMarketplace from './components/advisors/AdvisorMarketplace';
import LegacyAdvisorProfile from './components/advisors/AdvisorProfile';
import AdvisorApplicationForm from './components/advisors/AdvisorApplicationForm';
import AdvisorNetworkDiscovery from './pages/AdvisorNetwork/AdvisorDiscovery';
import AdvisorNetworkProfile from './pages/AdvisorNetwork/AdvisorProfile';
import AdvisorPostCompose from './pages/AdvisorNetwork/AdvisorPostCompose';
import AdvisorComposeGuard from './pages/AdvisorNetwork/AdvisorComposeGuard';
import axios from 'axios';
import { API_BASE_URL } from './config/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ToastProvider } from './components/common/Toast';
import ErrorBoundary from './components/common/ErrorBoundary';
import { UpgradeProvider } from './context/UpgradeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { ThemeProvider } from './context/ThemeContext';
import { AdviceSyncProvider } from './context/AdviceSyncContext';
import Upgrade from './pages/Upgrade';

/** Visible shell while redirecting after sign-in (avoids a blank frame). */
const PostAuthRedirect = ({ user }) => {
  const target =
    user?.portfolio_connected || user?.onboarding_complete ? '/dashboard' : '/onboarding';
  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center gap-3">
      <div className="w-10 h-10 border-2 border-hairline border-t-pine rounded-full animate-spin" />
      <p className="text-body text-secondary">Taking you in…</p>
      <Navigate to={target} replace />
    </div>
  );
};

// Wrapper component to handle vision detail page with URL params
const VisionDetailPageWrapper = ({ user }) => {
  const { visionId } = useParams();
  const navigate = useNavigate();
  const [vision, setVision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchVision = async () => {
    if (!visionId) return;
    try {
      const response = await axios.get(`/api/visions/${visionId}`);
      setVision(response.data);
    } catch (error) {
      console.error('Failed to fetch vision:', error);
      navigate('/visions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visionId) {
      setLoading(true);
      fetchVision();
    }
  }, [visionId, refreshKey]);

  const handleBack = () => {
    navigate('/visions');
  };

  const handleVisionUpdate = () => {
    setRefreshKey(k => k + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading vision…</p>
      </div>
    );
  }

  if (!vision) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-primary">Vision not found</p>
      </div>
    );
  }

  return (
    <VisionDetailPage
      vision={vision}
      onBack={handleBack}
      user={user}
      onVisionUpdate={handleVisionUpdate}
    />
  );
};

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = 120000; // 120 second timeout for AI operations
console.log('Setting axios baseURL to:', API_BASE_URL);
console.log('Current axios baseURL:', axios.defaults.baseURL);

// Add request interceptor for debugging
axios.interceptors.request.use(request => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Starting Request:', request.method?.toUpperCase(), request.url);
    console.log('Full URL:', request.baseURL + request.url);
    console.log('Request config:', {
      baseURL: request.baseURL,
      url: request.url,
      method: request.method,
      headers: request.headers
    });
  }
  return request;
});

// Add response interceptor for debugging
axios.interceptors.response.use(
  response => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Response:', response.status, response.config.url);
    }
    return response;
  },
  error => {
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    return Promise.reject(error);
  }
);

const GlobalAssistants = ({ user }) => {
  const location = useLocation();
  if (!user || !(user.portfolio_connected || user.onboarding_complete)) return null;
  // Cashflow has a full-width embedded Ask Aura panel; FAB would cover its Send button.
  if (location.pathname.startsWith('/cashflow')) return null;
  const section = location.pathname.split('/')[1] || 'dashboard';
  return (
    <>
      <AuraCoPilot user={user} />
      <AuraAssistant
        screenName={
          section === 'community' ? 'community' :
          section === 'profile' ? 'profile' :
          section
        }
        user={user}
      />
    </>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [error, setError] = useState(null);

  // Track mount status to avoid state updates on unmounted component
  const isMounted = useRef(true);
  const skipNextProfileFetch = useRef(false);
  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    console.log('API_BASE_URL:', API_BASE_URL);

    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      if (skipNextProfileFetch.current) {
        skipNextProfileFetch.current = false;
        if (isMounted.current) setLoading(false);
        return;
      }
      fetchUserProfile();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      if (isMounted.current) setLoading(false);
    }
  }, [authToken]);

  const fetchUserProfile = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/user/profile', { timeout: 20000 });
      if (isMounted.current) setUser(response.data);
    } catch (error) {
      if (!isMounted.current) return;
      if (error.response) {
        setError(`Server Error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        if (error.response.status === 401) {
          localStorage.removeItem('authToken');
          setAuthToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      } else if (error.request) {
        setError('Network Error: Cannot connect to server. Please check if the backend is running.');
      } else {
        setError(`Request Error: ${error.message}`);
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    if (!isMounted.current) return;
    if (!token || !userData?.user_id) {
      setError('Sign-in did not return a valid session. Check that the backend is running on port 3001.');
      setLoading(false);
      return;
    }
    try {
      skipNextProfileFetch.current = true;
      localStorage.setItem('authToken', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      setAuthToken(token);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Login state update error:', error);
      if (isMounted.current) {
        setError('Failed to update login state');
        setLoading(false);
      }
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('authToken');
      setAuthToken(null);
      setUser(null);
      setError(null);
      delete axios.defaults.headers.common['Authorization'];
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      if (isMounted.current) {
        setAuthToken(null);
        setUser(null);
        setError(null);
      }
    }
  };

  const safeSetError = (newError) => { if (isMounted.current) setError(newError); };
  const safeSetUser  = (newUser)  => { if (isMounted.current) setUser(newUser);  };

  // Only block the shell while restoring an existing session — never after a fresh sign-in.
  const hydratingSession = loading && authToken && !user;

  if (hydratingSession) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <div className="w-full h-full border-4 border-pine border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-secondary animate-pulse">Connecting to your Aura…</p>
          {error && (
            <div className="mt-4 p-4 border border-danger/40 bg-pine-tint rounded-lg max-w-md mx-auto">
              <p className="text-danger text-sm">{error}</p>
              <button
                type="button"
                onClick={() => { if (isMounted.current) window.location.reload(); }}
                className="mt-2 btn-danger text-sm"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary name="App" title="Something went wrong — refresh the page">
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <ToastProvider>
      <CurrencyProvider isAuthenticated={!!authToken}>
      <Router>
        <ThemeProvider>
        <AdviceSyncProvider user={user}>
        <UpgradeProvider user={user}>
        <div className="App min-h-screen bg-bone text-ink">

        {/* Error banner */}
        {error && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-danger surface-dark p-3 text-center border-b border-hairline">
            <span>{error}</span>
            <button 
              onClick={() => safeSetError(null)}
              className="ml-4 btn-secondary text-sm py-1 px-2"
            >
              Dismiss
            </button>
          </div>
        )}

        <Routes>
          <Route
            path="/"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : (
                <Navigate to={(user.portfolio_connected || user.onboarding_complete) ? "/dashboard" : "/onboarding"} replace />
              )
            }
          />

          <Route
            path="/auth"
            element={
              !user ? (
                <AuthScreen onLogin={handleLogin} />
              ) : (
                <PostAuthRedirect user={user} />
              )
            }
          />

          {/* Always allow forgot/reset even if logged in: email links should work */}
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          
          {/* OAuth redirect for Plaid (required for production) */}
          <Route path="/oauth-redirect" element={<PlaidOAuthRedirect />} />

          <Route 
            path="/onboarding" 
            element={
              user && !user.portfolio_connected && !user.onboarding_complete ? (
                <OnboardingFlow 
                  user={user} 
                  onComplete={(updated) => safeSetUser(updated || { ...user, portfolio_connected: true, onboarding_complete: true })} 
                />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            } 
          />

          <Route 
            path="/dashboard" 
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? "/onboarding" : "/auth"} replace />
              )
            } 
          />
          <Route
            path="/dashboard/insights"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <DashboardInsightsPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />

          <Route 
            path="/visions" 
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <VisionsPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            } 
          />

          <Route
            path="/community"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <Community user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />

          <Route
            path="/profile"
            element={
              user ? (
                <ProfilePage user={user} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/profile/:username"
            element={
              user && user.portfolio_connected ? (
                <ProfilePage user={user} />
              ) : (
                <Navigate to={user ? "/onboarding" : "/auth"} replace />
              )
            }
          />

          {/* Unified Learn & Play (Learning + Games with guidance) */}
          <Route
            path="/learn-and-play"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <LearningAndGamesHub userId={user.user_id} user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />
          <Route
            path="/learning"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <LearningHub userId={user.user_id} user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />

          <Route
            path="/learning/:categoryId"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <RoadmapView userId={user.user_id} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />

          <Route
            path="/learning/:categoryId/node/:nodeId"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <NodeContent userId={user.user_id} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />

          {/* Aura City of Lights Game Routes */}
          <Route
            path="/game/city-of-signals"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <LevelSelector user={user} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />
          <Route
            path="/game/city-of-signals/:level"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <CityOfSignalsGame user={user} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            }
          />

          {/* Learning Paths Routes */}
          <Route
            path="/learning/paths"
            element={user ? <LearningPaths user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/learning/paths/:pathId"
            element={user ? <LearningPaths user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/learning/paths/:pathId/lessons/:lessonId"
            element={user ? <LessonViewer user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />}
          />

          {/* Notes Route */}
          <Route
            path="/notes"
            element={user ? <NotesList user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />}
          />

          <Route
            path="/my-plan"
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <MyPlanPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to={user ? '/dashboard' : '/auth'} replace />
              )
            }
          />

          <Route 
            path="/vision/:visionId" 
            element={
              user && (user.portfolio_connected || user.onboarding_complete) ? (
                <VisionDetailPageWrapper user={user} />
              ) : (
                <Navigate to={user ? "/dashboard" : "/auth"} replace />
              )
            } 
          />

          <Route 
            path="/portfolio-xray" 
            element={
              user && user.portfolio_connected ? (
                <PortfolioXRay user={user} />
              ) : (
                <Navigate to={user ? "/onboarding" : "/auth"} replace />
              )
            } 
          />

          <Route 
            path="/cashflow" 
            element={
              user ? (
                <CashflowPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            } 
          />

          <Route 
            path="/account-settings" 
            element={
              user ? (
                <AccountSettings 
                  user={user} 
                  onBack={() => window.history.back()}
                  onUserUpdate={setUser}
                />
              ) : (
                <Navigate to="/auth" replace />
              )
            } 
          />

          <Route
            path="/upgrade"
            element={user ? <Upgrade user={user} /> : <Navigate to="/auth" replace />}
          />

          <Route
            path="/advisors"
            element={user ? <AdvisorMarketplace /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/advisors/apply"
            element={user ? <AdvisorApplicationForm /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/advisors/:advisorId"
            element={user ? <LegacyAdvisorProfile /> : <Navigate to="/auth" replace />}
          />

          <Route
            path="/advisor-network/compose"
            element={
              user ? (
                <AdvisorComposeGuard user={user}>
                  <AdvisorPostCompose user={user} />
                </AdvisorComposeGuard>
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/advisor-network/:advisor_id"
            element={<AdvisorNetworkProfile user={user} />}
          />
          <Route
            path="/advisor-network"
            element={<AdvisorNetworkDiscovery user={user} />}
          />

          <Route 
            path="/playground" 
            element={
              user && user.portfolio_connected ? (
                <PlaygroundContainer 
                  user={user} 
                  onClose={() => window.history.back()} 
                />
              ) : (
                <Navigate to={user ? "/onboarding" : "/auth"} replace />
              )
            } 
          />

          <Route 
            path="/" 
            element={
              <Navigate to={
                user 
                  ? ((user.portfolio_connected || user.onboarding_complete) ? "/dashboard" : "/onboarding")
                  : "/auth"
              } replace />
            } 
          />
        </Routes>
        
        {user && (user.portfolio_connected || user.onboarding_complete) && (
          <AskAuraHint user={user} />
        )}
        
        <GlobalAssistants user={user} />
        </div>
        </UpgradeProvider>
        </AdviceSyncProvider>
        </ThemeProvider>
      </Router>
      </CurrencyProvider>
    </ToastProvider>
    </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
